import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Calendar, Tag, Loader2, ExternalLink, ArrowLeft } from 'lucide-react';
import { meshNewsApi, brandsApi } from '../services/api';
import RichTextContent from '../components/RichTextContent';

const NewsArticlePage = () => {
  const { newsId } = useParams();
  const [article, setArticle] = useState(null);
  const [brands, setBrands] = useState([]);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articleRes, brandsRes, allNewsRes] = await Promise.all([
          meshNewsApi.get(newsId),
          brandsApi.getAll(),
          meshNewsApi.getAll()
        ]);
        setArticle(articleRes.data);
        setBrands(brandsRes.data);
        // Get 3 related news (excluding current)
        setRelatedNews(allNewsRes.data.filter(n => n.id !== newsId).slice(0, 3));
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [newsId]);

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-gray-900 mb-4">Article not found</h1>
          <Link to="/news" className="text-gray-600 hover:text-gray-900">
            Return to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="news-article-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-end">
        <div className="absolute inset-0 bg-gray-900">
          {article.image && (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-50"
              style={{ backgroundImage: `url(${article.image})` }}
            />
          )}
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/news" className="hover:text-white transition-colors">News</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white truncate max-w-[200px]">{article.title}</span>
          </nav>

          <div className="flex items-center flex-wrap gap-3 mb-4">
            <span className="inline-flex items-center px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full">
              <Tag className="h-3 w-3 mr-1" />
              {article.category}
            </span>
            {article.brandTags?.map(brandId => {
              const brandName = getBrandName(brandId);
              return brandName ? (
                <Link
                  key={brandId}
                  to={`/brand/${brandId}`}
                  className="inline-flex items-center px-3 py-1 bg-blue-500/80 text-white text-xs font-medium rounded-full hover:bg-blue-500 transition-colors"
                >
                  {brandName}
                </Link>
              ) : null;
            })}
            <span className="flex items-center text-sm text-gray-300">
              <Calendar className="h-4 w-4 mr-1" />
              {article.date}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-white leading-tight">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <RichTextContent 
              content={article.excerpt} 
              className="text-xl text-gray-600 leading-relaxed mb-8 [&_p]:mb-4"
            />
            
            {/* If there's a link, show CTA */}
            {article.link && (
              <div className="bg-gray-50 p-8 rounded-lg text-center my-12">
                <p className="text-gray-600 mb-4">Want to learn more about this story?</p>
                <a
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-medium rounded-sm hover:bg-gray-800 transition-colors"
                  data-testid="article-external-link"
                >
                  {article.linkText || 'Read Full Article'}
                  <ExternalLink className="ml-2 h-5 w-5" />
                </a>
              </div>
            )}

            {/* Tagged Brands */}
            {article.brandTags?.length > 0 && (
              <div className="border-t pt-8 mt-12">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Related Brands</h3>
                <div className="flex flex-wrap gap-4">
                  {article.brandTags.map(brandId => {
                    const brand = brands.find(b => b.id === brandId);
                    if (!brand) return null;
                    return (
                      <Link
                        key={brandId}
                        to={`/brand/${brandId}`}
                        className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div>
                          <div className="font-medium text-gray-900">{brand.name}</div>
                          <div className="text-sm text-gray-500">{brand.country}</div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Back to News */}
          <div className="mt-12 pt-8 border-t">
            <Link
              to="/news"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to All News
            </Link>
          </div>
        </div>
      </section>

      {/* Related News */}
      {relatedNews.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-medium text-gray-900 mb-8">More News</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase">
                        {item.category}
                      </span>
                      <span className="text-gray-300">•</span>
                      <span className="text-xs text-gray-500">{item.date}</span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-gray-700 transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsArticlePage;
